/**
 * TimeSlider - Time travel component for viewing map history
 *
 * Allows users to scrub through the history of their mind map
 */

import React, { useState, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, Play, Pause, RotateCcw, ChevronLeft, ChevronRight } from 'lucide-react';
import { format, differenceInDays, addDays } from 'date-fns';
import { useMapStore } from '../../stores/mapStore';

const TimeSlider = () => {
  const {
    timeRange,
    currentTime,
    isTimeTraveling,
    setCurrentTime,
    exitTimeTravel,
  } = useMapStore();

  const [isExpanded, setIsExpanded] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);

  // Calculate slider values
  const { start, end } = timeRange;
  const totalDays = useMemo(() => {
    if (!start || !end) return 0;
    return Math.max(1, differenceInDays(end, start));
  }, [start, end]);

  const currentProgress = useMemo(() => {
    if (!start || !currentTime) return 100;
    return (differenceInDays(currentTime, start) / totalDays) * 100;
  }, [start, currentTime, totalDays]);

  // Handle slider change
  const handleSliderChange = useCallback((e) => {
    const progress = parseFloat(e.target.value);
    const daysFromStart = Math.round((progress / 100) * totalDays);
    const newTime = addDays(start, daysFromStart);
    setCurrentTime(newTime.toISOString());
  }, [start, totalDays, setCurrentTime]);

  // Handle step navigation
  const handleStep = useCallback((direction) => {
    const daysFromStart = differenceInDays(currentTime, start);
    const newDays = Math.max(0, Math.min(totalDays, daysFromStart + direction));
    const newTime = addDays(start, newDays);
    setCurrentTime(newTime.toISOString());
  }, [start, currentTime, totalDays, setCurrentTime]);

  // Handle play/pause
  const handlePlayPause = useCallback(() => {
    setIsPlaying(!isPlaying);
    // TODO: Implement playback animation
  }, [isPlaying]);

  if (!start || !end) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-40">
      {/* Toggle button */}
      <div className="flex justify-center -mb-px">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className={`px-4 py-1.5 bg-gray-800 border border-gray-700 border-b-0 rounded-t-lg flex items-center gap-2 text-sm transition-colors ${
            isTimeTraveling ? 'text-indigo-400' : 'text-gray-400'
          } hover:text-white`}
        >
          <Clock size={14} />
          <span>Time Travel</span>
          {isTimeTraveling && (
            <span className="px-1.5 py-0.5 bg-indigo-500/20 rounded text-xs text-indigo-300">
              Active
            </span>
          )}
        </button>
      </div>

      {/* Slider panel */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="bg-gray-900/95 backdrop-blur border-t border-gray-800 overflow-hidden"
          >
            <div className="px-6 py-4">
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-4">
                  <h3 className="text-sm font-medium text-white">
                    Map History
                  </h3>
                  <span className="text-xs text-gray-500">
                    {format(start, 'MMM d, yyyy')} — {format(end, 'MMM d, yyyy')}
                  </span>
                </div>

                {isTimeTraveling && (
                  <button
                    onClick={exitTimeTravel}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-500 transition-colors"
                  >
                    <RotateCcw size={14} />
                    <span>Return to Present</span>
                  </button>
                )}
              </div>

              {/* Slider controls */}
              <div className="flex items-center gap-4">
                {/* Play/Pause */}
                <button
                  onClick={handlePlayPause}
                  className="p-2 bg-gray-800 rounded-lg hover:bg-gray-750 transition-colors"
                >
                  {isPlaying ? (
                    <Pause size={16} className="text-white" />
                  ) : (
                    <Play size={16} className="text-white" />
                  )}
                </button>

                {/* Step back */}
                <button
                  onClick={() => handleStep(-1)}
                  className="p-2 bg-gray-800 rounded-lg hover:bg-gray-750 transition-colors"
                >
                  <ChevronLeft size={16} className="text-white" />
                </button>

                {/* Slider */}
                <div className="flex-1 relative">
                  {/* Track */}
                  <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                    {/* Progress fill */}
                    <div
                      className="h-full bg-gradient-to-r from-indigo-600 to-purple-500"
                      style={{ width: `${currentProgress}%` }}
                    />
                  </div>

                  {/* Input slider (invisible but functional) */}
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={currentProgress}
                    onChange={handleSliderChange}
                    className="absolute inset-0 w-full opacity-0 cursor-pointer"
                  />

                  {/* Thumb indicator */}
                  <div
                    className="absolute top-1/2 -translate-y-1/2 -ml-2 w-4 h-4 bg-white rounded-full shadow-lg pointer-events-none"
                    style={{ left: `${currentProgress}%` }}
                  />
                </div>

                {/* Step forward */}
                <button
                  onClick={() => handleStep(1)}
                  className="p-2 bg-gray-800 rounded-lg hover:bg-gray-750 transition-colors"
                >
                  <ChevronRight size={16} className="text-white" />
                </button>

                {/* Current date display */}
                <div className="min-w-32 text-right">
                  <span className="text-sm text-white font-medium">
                    {currentTime && format(new Date(currentTime), 'MMM d, yyyy')}
                  </span>
                  <span className="block text-xs text-gray-500">
                    {currentTime && format(new Date(currentTime), 'h:mm a')}
                  </span>
                </div>
              </div>

              {/* Timeline markers */}
              <div className="flex justify-between mt-2 px-16">
                {[0, 25, 50, 75, 100].map((percent) => {
                  const markerDate = addDays(start, (percent / 100) * totalDays);
                  return (
                    <span key={percent} className="text-[10px] text-gray-600">
                      {format(markerDate, 'MMM d')}
                    </span>
                  );
                })}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TimeSlider;
